import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Info, RotateCw, Loader2 } from 'lucide-react';
import { CountrySelector } from './CountrySelector';
import { formatNumber } from '@/utils/numberFormat';

interface Column {
  header: string;
  accessor: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
  editable?: boolean;
  columnType?: 'text' | 'number' | 'select' | 'date' | 'select-with-other' | 'country';
  options?: string[]; // For select type
  optionsWithIcons?: Array<{ value: string; icon: any; displayValue?: string }>; // For select with icons
  primaryField?: boolean; // For mobile card view - marks fields to show when collapsed
  showTooltip?: boolean; // Enable tooltip for this column
  tooltipAccessor?: string; // Accessor for tooltip content in the data object
  tooltipRender?: (displayValue: React.ReactNode) => React.ReactNode; // Custom tooltip renderer wrapping the display value
}

interface SpreadsheetTableProps {
  columns: Column[];
  data: any[];
  onCellClick?: (rowIndex: number, columnKey: string, value: any) => void;
  onCellEdit?: (rowIndex: number, columnKey: string, newValue: any) => void;
  onRetryConversion?: (rowId: string) => Promise<void>;
}

export const SpreadsheetTable: React.FC<SpreadsheetTableProps> = ({
  columns,
  data,
  onCellClick,
  onCellEdit,
  onRetryConversion
}) => {
  const [editingCell, setEditingCell] = useState<{ row: number; col: string } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [isOtherSelected, setIsOtherSelected] = useState<boolean>(false);
  const [retryingRowId, setRetryingRowId] = useState<string | null>(null);

  const handleCellClick = (rowIndex: number, columnKey: string, value: any) => {
    const column = columns.find(col => col.accessor === columnKey);
    if (column?.editable) {
      setEditingCell({ row: rowIndex, col: columnKey });
      setEditValue(value?.toString() || '');

      // Check if it's select-with-other and if current value is a custom value
      if (column.columnType === 'select-with-other' && column.optionsWithIcons) {
        const standardOptions = column.optionsWithIcons.map(opt => opt.value);
        const isCustomValue = value && !standardOptions.includes(value);
        setIsOtherSelected(isCustomValue);
      } else {
        setIsOtherSelected(false);
      }
    } else if (onCellClick) {
      onCellClick(rowIndex, columnKey, value);
    }
  };

  const handleEditSave = (rowIndex: number, columnKey: string, valueOverride?: any) => {
    // Close immediately to prevent stuck state
    setEditingCell(null);

    if (onCellEdit) {
      const column = columns.find(col => col.accessor === columnKey);
      const valueToSave = valueOverride !== undefined ? valueOverride : editValue;

      // Only parse as number if it's a number column
      if (column?.columnType === 'number') {
        const numValue = parseFloat(valueToSave);
        onCellEdit(rowIndex, columnKey, isNaN(numValue) ? valueToSave : numValue);
      } else {
        // For text, date, and select - pass as string
        onCellEdit(rowIndex, columnKey, valueToSave);
      }
    }
  };

  const handleEditCancel = () => {
    setEditingCell(null);
  };
  return (
    <div className="w-full overflow-x-auto border border-gray-300 rounded-lg">
      <div className="min-w-full inline-block">
        <table className="w-full border-collapse">
          {/* Header */}
          <thead>
            <tr className="bg-gray-50 border-b border-gray-300">
              {columns.map((col, idx) => (
                <th
                  key={col.accessor}
                  style={{ width: col.width }}
                  className={`
                    px-3 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider
                    border-r border-gray-300 last:border-r-0
                    ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}
                  `}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody className="bg-white">
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-8 text-center text-sm text-gray-500"
                >
                  No data available
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr
                  key={row.id || rowIndex}
                  className="border-b border-gray-200 last:border-b-0 hover:bg-blue-50 transition-colors"
                >
                  {columns.map((col) => {
                    const value = row[col.accessor];
                    let displayValue = typeof value === 'number' ? formatNumber(value) : value || '-';
                    const isEditing = editingCell?.row === rowIndex && editingCell?.col === col.accessor;

                    // For select-with-other columns, find the icon and display value
                    let DisplayIcon = null;
                    if (col.columnType === 'select-with-other' && col.optionsWithIcons && value) {
                      const option = col.optionsWithIcons.find(opt => opt.value === value);
                      DisplayIcon = option?.icon;
                      // Use custom displayValue if provided, otherwise use the value
                      if (option?.displayValue) {
                        displayValue = option.displayValue;
                      }
                    }

                    return (
                      <td
                        key={`${row.id || rowIndex}-${col.accessor}`}
                        onClick={() => handleCellClick(rowIndex, col.accessor, value)}
                        className={`
                          px-3 py-2 text-sm text-gray-900 border-r border-gray-200 last:border-r-0
                          ${col.editable ? 'cursor-text hover:bg-blue-100' : 'cursor-pointer'} font-mono
                          ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}
                        `}
                      >
                        {isEditing ? (
                          <>
                            {col.columnType === 'select-with-other' && col.optionsWithIcons ? (
                              <>
                                {isOtherSelected ? (
                                  <Input
                                    type="text"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={() => handleEditSave(rowIndex, col.accessor)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleEditSave(rowIndex, col.accessor);
                                      } else if (e.key === 'Escape') {
                                        handleEditCancel();
                                      }
                                    }}
                                    placeholder="Enter payment method"
                                    autoFocus
                                    className="h-7 text-sm font-mono"
                                  />
                                ) : (
                                  <Select
                                    key={`select-${rowIndex}-${col.accessor}`}
                                    value={editValue}
                                    onValueChange={async (value) => {
                                      setEditValue(value);
                                      if (onCellEdit) {
                                        await onCellEdit(rowIndex, col.accessor, value);
                                      }
                                      setEditingCell(null);
                                    }}
                                    defaultOpen={true}
                                  >
                                    <SelectTrigger className="h-7 text-sm font-mono">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {col.optionsWithIcons.map((option) => {
                                        const Icon = option.icon;
                                        return (
                                          <SelectItem key={option.value} value={option.value}>
                                            <div className="flex items-center gap-2">
                                              {Icon && <Icon className="h-4 w-4" />}
                                              {option.displayValue || option.value}
                                            </div>
                                          </SelectItem>
                                        );
                                      })}
                                    </SelectContent>
                                  </Select>
                                )}
                              </>
                            ) : col.columnType === 'select' && col.options ? (
                              <Select
                                key={`select-${rowIndex}-${col.accessor}`}
                                value={editValue}
                                onValueChange={async (value) => {
                                  setEditValue(value);
                                  if (onCellEdit) {
                                    await onCellEdit(rowIndex, col.accessor, value);
                                  }
                                  setEditingCell(null);
                                }}
                                defaultOpen={true}
                              >
                                <SelectTrigger className="h-7 text-sm font-mono">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {col.options.map((option) => (
                                    <SelectItem key={option} value={option}>
                                      {option}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : col.columnType === 'date' ? (
                              <Input
                                type="date"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={() => handleEditSave(rowIndex, col.accessor)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleEditSave(rowIndex, col.accessor);
                                  } else if (e.key === 'Escape') {
                                    handleEditCancel();
                                  }
                                }}
                                autoFocus
                                className="h-7 text-sm font-mono"
                              />
                            ) : col.columnType === 'country' ? (
                              <CountrySelector
                                value={editValue}
                                onChange={async (value) => {
                                  setEditValue(value);
                                  if (onCellEdit) {
                                    await onCellEdit(rowIndex, col.accessor, value);
                                  }
                                  setEditingCell(null);
                                }}
                              />
                            ) : (
                              <Input
                                type={col.columnType === 'number' ? 'number' : 'text'}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={() => handleEditSave(rowIndex, col.accessor)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleEditSave(rowIndex, col.accessor);
                                  } else if (e.key === 'Escape') {
                                    handleEditCancel();
                                  }
                                }}
                                autoFocus
                                className="h-7 text-sm font-mono"
                              />
                            )}
                          </>
                        ) : row._conversionFailed && col.showTooltip && onRetryConversion ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-orange-600 hover:text-orange-700 hover:bg-orange-100 gap-1"
                            disabled={retryingRowId === row.id}
                            onClick={async (e) => {
                              e.stopPropagation();
                              setRetryingRowId(row.id);
                              try {
                                await onRetryConversion(row.id);
                              } finally {
                                setRetryingRowId(null);
                              }
                            }}
                          >
                            {retryingRowId === row.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RotateCw className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        ) : (
                          <>
                            {col.tooltipRender ? (
                              col.tooltipRender(displayValue)
                            ) : col.showTooltip && col.tooltipAccessor && row[col.tooltipAccessor] ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    className="inline-flex items-center gap-1 hover:bg-gray-100 px-1 py-0.5 rounded cursor-pointer"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {col.columnType === 'select-with-other' && DisplayIcon ? (
                                      <div className="flex items-center justify-center">
                                        <DisplayIcon className="h-5 w-5" />
                                      </div>
                                    ) : col.columnType === 'select-with-other' && !DisplayIcon && value ? (
                                      displayValue
                                    ) : (
                                      displayValue
                                    )}
                                    <Info className="h-3 w-3 text-muted-foreground" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-2">
                                  <p className="text-sm font-medium">{row[col.tooltipAccessor]}</p>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <>
                                {col.columnType === 'select-with-other' && DisplayIcon ? (
                                  <div className="flex items-center justify-center">
                                    <DisplayIcon className="h-5 w-5" />
                                  </div>
                                ) : col.columnType === 'select-with-other' && !DisplayIcon && value ? (
                                  // Custom value - show the text
                                  <span>{displayValue}</span>
                                ) : (
                                  displayValue
                                )}
                              </>
                            )}
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
